import { memo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { vars } from 'src/core/themes';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useListingAutoPricingSettings } from 'src/modals/ListingDetails/components/useListingAutoPricingSettings';
import { ProcessingIcon } from 'src/svgs/Viagogo';
import { STANDARD_DEVIATIONS_TO_CID } from 'src/utils/constants/contentIdMaps';
import { AutoPricingOutlierMode, Feature } from 'src/WebApiController';

import { useListingTableActionContextSubmitPricingSettingsChangeSelector } from '../../ListingTableActionContext/ListingTableActionContext.selectors';
import {
  ListingPricingInput,
  ListingPricingSettingsCellProps,
} from '../ListingPriceForm.types';
import { useCellDisabled, useIsSubmittingField } from '../useCellState';
import * as styles from './Cell.css';

const OutlierFilteringCellRaw: React.FC<ListingPricingSettingsCellProps> = ({
  listing,
  editable,
  event,
  isV2Variant = false,
}) => {
  const hasReworkListingPriceFormFeature = useUserHasFeature(
    Feature.ReworkListingPriceForm
  );
  const formMethods = useFormContext<ListingPricingInput>();
  const { watch, formState, getFieldState, setValue } = formMethods;
  const onSubmitPricingSettingsChange =
    useListingTableActionContextSubmitPricingSettingsChangeSelector();

  const {
    outlierMode,
    outlierStandardDeviations,
    canAutoPrice,
    onOutlierChange,
  } = useListingAutoPricingSettings(listing);

  const rowIndex = watch('rowIndex');
  const isDisabled = useCellDisabled(
    listing,
    canAutoPrice,
    editable,
    event.virtId,
    outlierMode !== AutoPricingOutlierMode.StandardDeviations
  );
  const isSubmittingField = useIsSubmittingField('outlierStandardDeviations');

  const onUpdateOutlierFilter = useCallback(
    (value: string | null) => {
      const sanitized = parseInt(value ?? '');
      if (value != null && !isNaN(sanitized)) {
        onOutlierChange({
          outlierModeNew: AutoPricingOutlierMode.StandardDeviations,
          outlierStandardDeviationsNew: sanitized,
        });
      } else {
        onOutlierChange({
          outlierModeNew: AutoPricingOutlierMode.StandardDeviations,
          outlierStandardDeviationsNew: null,
        });
      }
      if (hasReworkListingPriceFormFeature) {
        onSubmitPricingSettingsChange({
          listing,
          formMethods,
          fieldName: 'outlierStandardDeviations',
        });
      } else {
        setValue('isSubmittingPricingSettings', true);
        setValue('isSubmittingRowIndex', rowIndex);
        setValue('submittingField', 'outlierStandardDeviations');
      }
    },
    [
      onOutlierChange,
      hasReworkListingPriceFormFeature,
      onSubmitPricingSettingsChange,
      listing,
      formMethods,
      rowIndex,
      setValue,
    ]
  );

  const content = (
    <PosFormField
      errors={
        getFieldState('outlierStandardDeviations', formState)?.error?.message
      }
      showErrorsInline
      className={styles.formFieldContainer}
    >
      {isSubmittingField ? (
        <div className="operation-button">
          <RotatingWrapper>
            <ProcessingIcon size={vars.iconSize.m} />
          </RotatingWrapper>
        </div>
      ) : (
        <PosEnumSelect
          className={
            isV2Variant ? styles.formSelectFieldV2 : styles.formSelectField
          }
          value={outlierStandardDeviations?.toString()}
          disabled={isDisabled}
          valueOptionsContent={STANDARD_DEVIATIONS_TO_CID}
          enableEmptySelection={true}
          onClick={(e) => e.stopPropagation()}
          onChange={onUpdateOutlierFilter}
        />
      )}
    </PosFormField>
  );

  return isV2Variant ? (
    content
  ) : (
    <TableCellDiv align="right" showTooltip={false}>
      {content}
    </TableCellDiv>
  );
};

export const OutlierFilteringCell = memo<ListingPricingSettingsCellProps>(
  OutlierFilteringCellRaw
);
